
import {Component, Prop, Watch} from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {COL, sqlSum, sqlSumByDocType, getTableName} from '@/worker/db/sqlHelpers';
import {PermissionsGroup} from '@/store/models.def';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import CustomerListFilter from '@/components/reports-v3/components/filters/CustomerListFilter.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import moment from 'moment';

@Component({
	components: {
		TableFilterable,
		CustomerListFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SharedStockItemSalesByCustomerTable extends FilterWidget {
	@Prop({ default: false })
	public forceSlModuleOnly!: boolean;

	public get permissionIds(): PermissionsGroup[] {
		return ['stocks', 'sales'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
        | 'docproject'
        | 'dtlproject'
	> = ['dateRange', 'stockItems', 'customers'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
	public selectedCustomersList: string[] = [];



	public get exportFileName() {
		const formattedDateRange = [
			moment(this.selectedDateRange[0]).format('DD MMM YY'),
			moment(this.selectedDateRange[1]).format('DD MMM YY'),
		];
		return (
			'Stock Item Sales By Customer' +
			'_' +
			formattedDateRange[0] +
			' to ' +
			formattedDateRange[1]
		);
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		// If forceSlModuleOnly is true, always use SL module
		return this.forceSlModuleOnly || settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedCustomersList,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
			selectedDateRange,
			selectedCustomers,
			selectedDocProjects,
			selectedDTLProjects,
			selectedStockItems,
		} = this;
		return JSON.stringify([
			selectedCustomersList,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
			selectedDateRange,
			selectedCustomers,
			selectedDocProjects,
			selectedDTLProjects,
			selectedStockItems,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		// Build filter clauses manually for cross-table UNION
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

		let customerFilter = '';
		if (this.selectedCustomersList.length > 0) {
			const list = this.selectedCustomersList.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND ${COL.CODE} IN (${list})`;
		}

		let stockItemFilter = '';
		if (this.selectedStockItems.length > 0) {
			const list = this.selectedStockItems.map((s) => `'${escapeStr(s)}'`).join(',');
			stockItemFilter = ` AND ${COL.ITEMCODE} IN (${list})`;
		}

		let docProjectFilter = '';
		if (this.selectedDocProjects.length > 0) {
			const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
			docProjectFilter = ` AND "DOCPROJECT" IN (${list})`;
		}

		let dtlProjectFilter = '';
		if (this.selectedDTLProjects.length > 0) {
			const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
			dtlProjectFilter = ` AND "DTLPROJECT" IN (${list})`;
		}

		// Sales filter includes docType restriction
		const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
		const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((d) => `'${d}'`).join(',')})`;

		const salesFilter = `${dateFilter}${customerFilter}${stockItemFilter}${docProjectFilter}${dtlProjectFilter}${docTypeFilter}`;
		const cnFilter = `${dateFilter}${customerFilter}${stockItemFilter}${docProjectFilter}${dtlProjectFilter}`;
		const arBaseFilter = `${dateFilter}${customerFilter}${docProjectFilter}${dtlProjectFilter}`;

		// Get table names
		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');
		const stockTable = getTableName('globalStocks');

		// Get a head for executing queries (ensures all tables are loaded)
		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded([
			'sales', 'creditNotes', 'customerTrades', 'globalStocks',
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// Calculate AR Document amount if in AR mode
		let arDocumentAmount = 0;
		if (!this.slPhModuleOnly) {
			// Single query for AR vs SL totals comparison
			const totalsData = await queryHead.rawQuery(`
				WITH ar_totals AS (
					SELECT
						SUM(CASE WHEN ${COL.DOCTYPE} = 'IV' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as iv_amt,
						SUM(CASE WHEN ${COL.DOCTYPE} = 'DN' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as dn_amt,
						SUM(CASE WHEN ${COL.DOCTYPE} = 'CN' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as cn_amt
					FROM ${arTable}
					WHERE ${arBaseFilter}
				),
				sl_totals AS (
					SELECT
						SUM(amount) as sl_amt,
						SUM(cn_amt) as sl_cn_amt
					FROM (
						SELECT TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount, 0 as cn_amt
						FROM ${salesTable} WHERE ${salesFilter}
						UNION ALL
						SELECT 0 as amount, TRY_CAST(${COL.AMOUNT} AS DOUBLE) as cn_amt
						FROM ${cnTable} WHERE ${cnFilter}
					) AS combined
				)
				SELECT
					(SELECT iv_amt FROM ar_totals) as ar_iv,
					(SELECT dn_amt FROM ar_totals) as ar_dn,
					(SELECT cn_amt FROM ar_totals) as ar_cn,
					(SELECT sl_amt FROM sl_totals) as sl_amt,
					(SELECT sl_cn_amt FROM sl_totals) as sl_cn_amt
			`);

			const row = totalsData[0] || {};
			const arIvAmount = Number(row.ar_iv) || 0;
			const arDnAmount = Number(row.ar_dn) || 0;
			const arCnAmount = Number(row.ar_cn) || 0;

			const arTotal = arIvAmount
				+ (this.includeDebitNote ? arDnAmount : 0)
				- (this.includeCreditNote ? arCnAmount : 0);

			const slTotal = Number(row.sl_amt) || 0;
			const slCnTotal = this.includeCreditNote ? (Number(row.sl_cn_amt) || 0) : 0;
			const slAmount = slTotal - slCnTotal;

			arDocumentAmount = arTotal - slAmount;
		}
		this.generateLoadingText(0.5, 0.9);

		// Single UNION query for sales + creditNotes by itemCode, plus stock descriptions
		let stockCodeFilter = '';
		if (this.selectedStockItems.length > 0) {
			const list = this.selectedStockItems.map((s) => `'${escapeStr(s)}'`).join(',');
			stockCodeFilter = ` AND ${COL.CODE} IN (${list})`;
		}

		const [itemData, stockItems] = await Promise.all([
			queryHead.rawQuery(`
				SELECT item_code,
					SUM(amount) as amount,
					SUM(quantity) as quantity,
					SUM(profit) as profit,
					SUM(cn_amount) as cn_amount,
					SUM(cn_quantity) as cn_quantity,
					SUM(cn_profit) as cn_profit
				FROM (
					SELECT ${COL.ITEMCODE} as item_code,
						TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
						TRY_CAST(${COL.QUANTITY} AS DOUBLE) as quantity,
						TRY_CAST(${COL.PROFIT} AS DOUBLE) as profit,
						0 as cn_amount, 0 as cn_quantity, 0 as cn_profit
					FROM ${salesTable} WHERE ${salesFilter}
					UNION ALL
					SELECT ${COL.ITEMCODE} as item_code,
						0 as amount, 0 as quantity, 0 as profit,
						TRY_CAST(${COL.AMOUNT} AS DOUBLE) as cn_amount,
						TRY_CAST(${COL.QUANTITY} AS DOUBLE) as cn_quantity,
						TRY_CAST(${COL.PROFIT} AS DOUBLE) as cn_profit
					FROM ${cnTable} WHERE ${cnFilter}
				) AS combined
				GROUP BY item_code
			`),
			queryHead.rawQuery(`
				SELECT ${COL.CODE} as item_code, "DESCRIPTION" as item_desc
				FROM ${stockTable}
				WHERE 1=1${stockCodeFilter}
			`),
		]);
		this.generateLoadingText(0.95);

		// Build stock description lookup
		const stockDescMap: Map<string, string> = new Map();
		for (const stock of stockItems) {
			stockDescMap.set(stock.item_code, stock.item_desc || '----');
		}

		// Build table items
		this.tableFields = [
			{ key: 'item_code', sortable: true },
			{ key: 'item_description', sortable: true },
			{ key: 'amount', sortable: true, formatter: TableItemFormatter.currencySplit },
			{ key: 'profit', sortable: true, formatter: TableItemFormatter.currencySplit },
			{ key: 'profit_margin', sortable: true, formatter: TableItemFormatter.percentageWithDecimal },
			{ key: 'quantity', sortable: true, formatter: TableItemFormatter.basicInteger },
			{ key: 'price', sortable: true, formatter: TableItemFormatter.basicWithDecimals },
		];

		this.tableItems = itemData
			.map((row) => {
				const itemCode = row.item_code;
				const saleAmount = Number(row.amount) || 0;
				const saleQuantity = Number(row.quantity) || 0;
				const saleProfit = Number(row.profit) || 0;
				const cnAmount = Number(row.cn_amount) || 0;
				const cnQuantity = Number(row.cn_quantity) || 0;
				const cnProfit = Number(row.cn_profit) || 0;

				const itemAmount = this.includeCreditNote ? saleAmount - cnAmount : saleAmount;
				const itemQuantity = this.includeCreditNote ? saleQuantity - cnQuantity : saleQuantity;
				const itemProfit = this.includeCreditNote ? saleProfit - cnProfit : saleProfit;

				if (itemQuantity === 0) {
					return null;
				}

				return {
					item_code: itemCode,
					item_description: stockDescMap.get(itemCode) || '----',
					amount: itemAmount,
					profit: itemProfit,
					profit_margin: itemAmount !== 0 ? (itemProfit / itemAmount) * 100 : 0,
					quantity: itemQuantity,
					price: itemQuantity !== 0 ? itemAmount / itemQuantity : 0,
				};
			})
			.filter((item) => item !== null);

		// Add AR Document row if in AR mode and there's a difference
		if (!this.slPhModuleOnly && arDocumentAmount !== 0) {
			this.tableItems.push({
				item_code: '*AR-Docs',
				item_description: 'Customer Module Documents',
				amount: arDocumentAmount,
				profit: arDocumentAmount,
				profit_margin: 100,
				quantity: 0,
				price: 0,
			});
		}

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
